import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import each from 'lodash/each';
import createLogger from 'vuex/dist/logger'
import provinces from './provinces'
import departments from './departments'
import vehicleTypes from './vehicleTypes'
import areas from './areas'
import settings from './settings'

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    /* Panel Company */
    panelCompanyName: 'BFY GO',
    panelCompanySortName: 'GO',
    bfyHost: '',

    /* NavBar */
    isNavBarVisible: true,

    /* FooterBar */
    isFooterBarVisible: true,

    /* Aside */
    isAsideVisible: true,
    isAsideExpanded: true,
    isAsideMobileExpanded: false,
    asideActiveForcedKey: null,
    asideActiveMenuKey: '',
    isAsideRightVisible: false,

    /* Updates */
    hasUpdates: false,

    /* Overlay */
    isOverlayVisible: false,

    boardRefresh: false,

    /* User */
    status: '',
    token: localStorage.getItem('token') || '',
    user: { },
    userName: null,
    permissions: [],
    roles: [],
    specifications: [],
  },
  mutations: {
    /* A fit-them-all commit */
    basic (state, payload) {
      state[payload.key] = payload.value;
    },

    /* Full Page mode */
    fullPage (state, payload) {
      state.isNavBarVisible = !payload;
      state.isAsideVisible = !payload;
      state.isFooterBarVisible = !payload;

      each(['has-aside-left', 'has-navbar-fixed-top'], htmlClass => {
        if (payload) {
          document.documentElement.classList.remove(htmlClass);
        } else {
          document.documentElement.classList.add(htmlClass);
        }
      });
    },

    /* Aside Desktop */
    asideStateToggle (state, payload = null) {
      const htmlAsideClassName = 'has-aside-expanded';

      let isExpand;

      if (payload !== null) {
        isExpand = payload;
      } else {
        isExpand = !state.isAsideExpanded;
      }

      if (isExpand) {
        document.documentElement.classList.add(htmlAsideClassName);
      } else {
        document.documentElement.classList.remove(htmlAsideClassName);
      }

      state.isAsideExpanded = isExpand;
    },

    /* Aside Mobile */
    asideMobileStateToggle (state, payload = null) {
      const htmlClassName = 'has-aside-mobile-expanded';

      let isShow;

      if (payload !== null) {
        isShow = payload;
      } else {
        isShow = !state.isAsideMobileExpanded;
      }

      if (isShow) {
        document.documentElement.classList.add(htmlClassName);
      } else {
        document.documentElement.classList.remove(htmlClassName);
      }

      state.isAsideMobileExpanded = isShow;
    },

    /* Aside Forced Active Key (when secondary submenu is open) */
    asideActiveForcedKeyToggle (state, payload) {
      state.asideActiveForcedKey = payload && payload.menuSecondaryKey ? payload.menuSecondaryKey : null;
    },

    setAsideActiveMenuKey (state, payload) {
      state.asideActiveMenuKey = payload;
    },

    boardRefresh (state, value) {
      state.boardRefresh = value;
    },

    /* Aside Right */
    asideRightToggle (state, payload = null) {
      const htmlClassName = 'has-aside-right';

      let isShow;

      if (payload !== null) {
        isShow = payload;
      } else {
        isShow = !state.isAsideRightVisible;
      }

      if (isShow) {
        document.documentElement.classList.add(htmlClassName);
        axios.put('notifications/read');
      } else {
        document.documentElement.classList.remove(htmlClassName);
      }

      state.isAsideRightVisible = isShow;
      state.hasUpdates = false;
    },

    hasUpdates (state, payload = null) {
      if (payload) {
        state.hasUpdates = payload;
      } else {
        state.hasUpdates = !state.hasUpdates;
      }
    },

    setRoles (state, payload = null) {
      if (payload) {
        state.roles = [];
        payload.forEach((value) => {
          state.roles.push({
            id: value.id,
            name: value.attributes.name
          });
        });
      }
    },

    /* Overlay */
    overlayToggle (state, payload = null) {
      if (payload === null) {
        payload = !state.isOverlayVisible;
      }

      state.isOverlayVisible = !!payload;
    },
    auth_request (state) {
      state.status = 'loading';
    },
    auth_success (state, { token, user }) {
      state.status = 'success';
      state.token = token;
      state.user = user.data;
      state.permissions = user.data.attributes.permissions;
      state.userName = user.data.attributes.name;
    },
    auth_error (state) {
      state.status = 'error';
    },
    logout (state) {
      state.status = '';
      state.token = '';
      state.user = { };
      state.permissions = [];
      localStorage.setItem('token', '');
      localStorage.setItem('tenent_id', '');
      localStorage.setItem('base_url', '');
    },
    set_user (state, user) {
      state.user = user;
      state.permissions = user.attributes.permissions;
      state.userName = user.attributes.name;
    },
    set_tenant (state, info) {
      state.panelCompanyName = info.appname
      state.panelCompanySortName = info.appsortname
      state.bfyHost = info.bfy_host
    },
    setSpecifications (state, payload = []) {
      state.specifications = payload;
    },
  },
  actions: {
    login ({ commit, dispatch }, userData) {
      return new Promise((resolve, reject) => {
        commit('auth_request');
        const baseURL = '//' + userData.company + '.' + process.env.VUE_APP_MAIN_DOMAIN;
        axios.defaults.baseURL = `${baseURL}/api/`;
        window.localStorage.setItem('base_url', baseURL);

        axios.post('auth/login', userData)
          .then((results) => {
            const { data: { token, user, tenant_id } } = results;
            localStorage.setItem('token', token);
            localStorage.setItem('tenent_id', tenant_id);
            localStorage.setItem('company', userData.company);
            commit('auth_success', { token, user });
            dispatch('settings/index')
            resolve(results);
          })
          .catch((error) => {
            console.error(error);
            commit('auth_error');
            localStorage.removeItem('token');
            reject(error);
          });
      });
    },
    logout ({ commit }) {
      return new Promise((resolve) => {
        commit('logout');
        localStorage.removeItem('token');
        localStorage.removeItem('base_url');
        localStorage.removeItem('tenent_id');
        delete axios.defaults.headers.common.Authorization;
        commit('settings/SET_SETTINGS', [])
        resolve();
      });
    },
    getRoles ({ commit }) {
      axios.get('list-roles')
        .then(response => {
          commit('setRoles', response.data.data);
      });
    },
    async getUserInfo ({ commit }) {
      try {
        const results = await axios.get('auth/me');
        const { data } = results.data;
        commit('set_user', data);
      } catch (e) {
        if (e.message.indexOf('401') > 0) {
          this.dispatch('logout');
          document.location = '/login';
        }
      }
    },
    async getTenant ({ commit }) {
      const results = await axios.get('info');
      commit('set_tenant', results.data);
    },
    async getSpecifications ({ commit }) {
      try {
        const { data } = await axios.get('specifications');
        commit('setSpecifications', data.data);
      } catch (e) {
        //
      }
    },
  },
  getters: {
    isLoggedIn: state => !!state.token,
    authStatus: state => state.status,
    authUser: state => state.user,
    authPermissions: state => state.permissions,
    getSpecifications: state => type => state.specifications.filter(specification => specification.type === type),
    logo: state => {
      if (state.panelCompanySortName === 'umraniye') {
        return 'umraniye-logo.png'
      }

      if (state.panelCompanySortName === 'oski') {
        return 'oski-logo.jpg'
      }

      return ''
    },
  },
  modules: {
    provinces,
    departments,
    vehicleTypes,
    settings,
    areas,
  },
  plugins: process.env.NODE_ENV === 'production' ? [] : [createLogger()],
});
